/**
 * The Google permissions a Symphony calendar connection asks for, split by what a connection cannot
 * work without.
 *
 * Google returns the UNION of every scope an account has already granted this OAuth client, so an
 * account that has consented before looks identical whatever is requested here. A shortened list only
 * shows up on a FIRST-time connect — as a grant missing a scope the assistant needs weeks later — which
 * is why the list is one shared value rather than whatever each surface happens to need.
 *
 * iOS (`SocialAuth.googleCalendarCode`), Android (`CalendarConnect.kt`) and the RN app
 * (`use-google-authenticator.ts`) each restate it in their own language because their SDKs take it at
 * compile time. Web reads it from here.
 */

/**
 * Every scope here must be one the Google project DECLARES, and that is the whole constraint.
 *
 * Google refuses a consent that asks for a sensitive scope the project has not had approved, and counts
 * the user against a per-project cap that is lifetime and cannot be reset. A scope the console does not
 * list at all is unapproved by definition — so asking for one costs a connection AND a slot, for a
 * permission nobody can use anyway.
 *
 * Two contacts scopes used to be here and were requested long after the console stopped declaring them.
 * They bought contact-photo avatars, which had not completed a single sync in the fortnight before they
 * went: adding a scope is cheap to write and expensive to keep, and nothing failed loudly enough to
 * notice. Check the project's Data Access page before adding one back.
 */

/**
 * Google grants these alongside any consent and never offers them as a choice, so nothing downstream has
 * to handle their absence. Nylas still needs them: it resolves the account's email and profile from the
 * refresh token, and fails with "Cannot get profile (user info, email) from given refresh token" without.
 */
const SIGN_IN_SCOPES = ['openid', 'https://www.googleapis.com/auth/userinfo.email', 'https://www.googleapis.com/auth/userinfo.profile'];

/**
 * The calendar scopes a connect ASKS for — read availability, write events.
 *
 * Google asks for each non-sign-in scope as its own CHECKBOX, unticked by default, and there is no way to
 * pre-tick one or mark it mandatory (`enable_granular_consent` is documented as "Deprecated, no effect if
 * set"). So a user can complete the whole flow granting any subset of these, and the code we get back
 * still exchanges for a usable refresh token — which is why Google's guidance is that the app checks what
 * it was actually granted.
 *
 * Expected, NOT required: a grant short of this list is reported, not refused. Nine hours of production
 * grants had 2 of 19 carrying `calendar.events` without `calendar.readonly`, both from the phone, and
 * whether such a connection works has never been tested — events can still be read and written with the
 * first alone. Refusing them would block a fifth of real connects on an assumption.
 */
export const EXPECTED_GOOGLE_CALENDAR_CONNECT_SCOPES = ['https://www.googleapis.com/auth/calendar.events', 'https://www.googleapis.com/auth/calendar.readonly'];

/**
 * Scopes a connection is better with and works without — declining one costs a feature, not the
 * connection. Refusing a connect over one of these would reject a perfectly usable calendar.
 */
export const OPTIONAL_GOOGLE_CALENDAR_CONNECT_SCOPES = ['https://www.googleapis.com/auth/admin.directory.resource.calendar.readonly'];
export const GOOGLE_CALENDAR_CONNECT_SCOPES = [...SIGN_IN_SCOPES, ...EXPECTED_GOOGLE_CALENDAR_CONNECT_SCOPES, ...OPTIONAL_GOOGLE_CALENDAR_CONNECT_SCOPES];

/**
 * The Gmail permissions a Google connect asks for when it is asking for Gmail at all.
 *
 * `gmail.send` is SENSITIVE; `gmail.readonly` is RESTRICTED, which additionally pulls in Google's Limited
 * Use terms and an annual third-party security assessment. Neither is approved on the project yet, so a
 * connect requests them only where `gmail-scopes-rollout` says to: an unapproved scope on a consent
 * screen spends that account against a per-project cap that is lifetime and cannot be reset.
 *
 * Asked for by the BROWSER only, and deliberately: iOS and Android restate their scope lists in their own
 * source at compile time, so putting an unapproved scope on a phone would ship it to store review with no
 * way to turn it off per account. They keep asking for calendar alone until Google approves these, at
 * which point the single Google connector covers all three surfaces at once. Written down here rather
 * than left as a silent gap, per the three-surface rule.
 */
export const GOOGLE_GMAIL_CONNECT_SCOPES = ['https://www.googleapis.com/auth/gmail.send', 'https://www.googleapis.com/auth/gmail.readonly'];

/**
 * What one Google connect should ask for. The single place that answers it, so the browser cannot request
 * a different set from the one the server is prepared to redeem and store.
 *
 * Adding a scope here changes what EXISTING users see on a re-consent, which is why the Gmail half is
 * behind a caller-supplied decision rather than simply appended to the shared list.
 */
export function googleConnectScopes(options) {
  return options.includeGmail ? [...GOOGLE_CALENDAR_CONNECT_SCOPES, ...GOOGLE_GMAIL_CONNECT_SCOPES] : GOOGLE_CALENDAR_CONNECT_SCOPES;
}

/** Every Google calendar scope begins with this — `calendar`, `calendar.events`, `calendar.readonly`,
 *  `calendar.settings.readonly` and the rest. */
const CALENDAR_SCOPE_PREFIX = 'https://www.googleapis.com/auth/calendar';

/** The wire value naming a connect the user completed without granting any calendar access at all. */
export const CALENDAR_SCOPES_DECLINED = 'calendar_scopes_declined';
function scopeList(granted) {
  return (typeof granted === 'string' ? granted.split(' ') : granted ?? []).filter(Boolean);
}

/**
 * Whether a grant can reach a calendar AT ALL — the only thing worth refusing a connect over, because it
 * is the only thing shown to make one useless. Measured in production: a grant of
 * `openid userinfo.email userinfo.profile` was stored and reported as Connected, and it cannot read an
 * event, list a calendar or write anything.
 *
 * Deliberately a PREFIX test rather than a list to match. Google returns what it granted, which need not
 * be what was asked for: an account that once consented to the single wide `calendar` scope carries that
 * and not the narrow pair, and asking for the wide one instead is itself on the table (one non-sign-in
 * scope is the only way Google shows no checkboxes at all). A list to match would refuse every one of
 * those, over a requirement spelled in scopes the grant never names.
 */
export function grantsGoogleCalendarAccess(granted) {
  return scopeList(granted).some(scope => scope.startsWith(CALENDAR_SCOPE_PREFIX));
}

/**
 * Which of [EXPECTED_GOOGLE_CALENDAR_CONNECT_SCOPES] a grant does not carry. Reported, never refused —
 * see that list for why. Empty when the grant is everything the connect asked for.
 *
 * Matched on the exact scope URL. Google normalizes the short sign-in aliases (`email` comes back as
 * `.../userinfo.email`), which is a reason not to validate those rather than a reason to alias here: the
 * calendar scopes have no short form and come back exactly as requested.
 */
export function missingExpectedGoogleCalendarScopes(granted) {
  const effective = new Set(scopeList(granted));
  return EXPECTED_GOOGLE_CALENDAR_CONNECT_SCOPES.filter(scope => !effective.has(scope));
}

/**
 * The OAuth client a browser calendar connect authorizes against — the verified scheduling-assistant
 * client, whose consent screen reads "Symphony by Wix".
 *
 * It is the only one of our two Google clients that can serve a browser: `symphony.wix.com` is an
 * authorized JavaScript origin on it, while the wix-login client the native apps mint through has no
 * browser origin at all and is owned by Wix Identity.
 *
 * A refresh token is bound to the client that minted it, so the server's code exchange must use this
 * same client — it reads the matching id and secret from its own databag
 * (`scheduling-assistant-google-client-id` / `-secret`). Not a secret: a client id is public, and it is
 * already compiled into the native apps.
 */
export const SCHEDULING_ASSISTANT_GOOGLE_CLIENT_ID = '373512099748-lkm9uviab6odip0u5ud94c9tonj1tatf.apps.googleusercontent.com';
//# sourceMappingURL=google-calendar-connect.js.map